import React from 'react';
import { Link } from 'react-router-dom';
import TeamCard from '../components/TeamCard';
import useScrollAnimation from '../hooks/useScrollAnimation';
import siteContent from '../content';

const { hero, story, team: teamSection, values, cta } = siteContent.pages.about.sections;
const { team } = siteContent.global;

const About: React.FC = () => {
  const storySection = useScrollAnimation({ threshold: 0 });
  const teamAnimation = useScrollAnimation({ threshold: 0 });
  const valuesSection = useScrollAnimation({ threshold: 0 });

  return (
    <>
      <title>About Us | Kline Real Estate Works</title>
      <meta
        name="description"
        content="Meet the Kline Real Estate Works team. Dedicated professionals with years of local market expertise committed to exceptional service in Royersford, PA."
      />
      <meta property="og:title" content="About Us | Kline Real Estate Works" />
      <meta property="og:description" content="Meet the Kline Real Estate Works team. Dedicated professionals with years of local market expertise committed to exceptional service in Royersford, PA." />
      <meta property="og:image" content="https://keithklinegroup.com/og-image.jpg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:image" content="https://keithklinegroup.com/og-image.jpg" />

      {/* Hero Section */}
      <section
        className="hero hero-with-image"
        data-cms-page="about"
        data-cms-section="hero"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 43, 92, 0.75), rgba(0, 43, 92, 0.85)), url('${hero.image}')`
        }}
        data-cms-field="image"
        data-cms-type="image"
      >
        <div className="hero-content">
          <p className="hero-subtitle" data-cms-field="subtitle">{hero.subtitle}</p>
          <h1 data-cms-field="heading">{hero.heading}</h1>
          <p data-cms-field="description">{hero.description}</p>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="section" data-cms-page="about" data-cms-section="story">
        <div className="container">
          <div
            ref={storySection.ref}
            className={`two-column scroll-fade-up ${storySection.isVisible ? 'visible' : ''}`}
          >
            <div>
              <h2 data-cms-field="heading">{story.heading}</h2>
              <div data-cms-list="paragraphs">
                {story.paragraphs.map((paragraph, index) => (
                  <p key={index} data-cms-field="paragraphs" data-cms-index={index}>
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
            <div
              style={{
                backgroundColor: 'var(--color-navy)',
                borderRadius: '16px',
                padding: '3rem',
                textAlign: 'center'
              }}
            >
              <img
                src="/logo.svg"
                alt="Kline Real Estate Works"
                style={{
                  maxWidth: '280px',
                  width: '100%',
                  margin: '0 auto'
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="section section-bg" data-cms-page="about" data-cms-section="team">
        <div className="container">
          <div
            ref={teamAnimation.ref}
            className={`scroll-fade-up ${teamAnimation.isVisible ? 'visible' : ''}`}
          >
            <div className="section-header">
              <h2 data-cms-field="heading">{teamSection.heading}</h2>
              <p data-cms-field="subheading">{teamSection.subheading}</p>
            </div>
            <div className="grid grid-4" data-cms-list="team" data-cms-page="global" data-cms-section="team">
              {team.map((member, index) => (
                <TeamCard key={member.id} member={member} showBio={true} index={index} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section" data-cms-page="about" data-cms-section="values">
        <div className="container">
          <div
            ref={valuesSection.ref}
            className={`scroll-fade-up ${valuesSection.isVisible ? 'visible' : ''}`}
          >
            <div className="section-header">
              <h2 data-cms-field="heading">{values.heading}</h2>
              <p data-cms-field="subheading">{values.subheading}</p>
            </div>
            <div className="services-grid" data-cms-list="items">
              {values.items.map((item, index) => (
                <div
                  key={index}
                  className="service-card"
                  data-cms-index={index}
                >
                  <span className="material-icons service-icon" data-cms-field="items" data-cms-index={index} data-cms-item-field="icon" data-cms-field-type="icon">{item.icon}</span>
                  <h3 data-cms-field="items" data-cms-index={index} data-cms-item-field="title">{item.title}</h3>
                  <p data-cms-field="items" data-cms-index={index} data-cms-item-field="description">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section section-navy" data-cms-page="about" data-cms-section="cta">
        <div className="container">
          <div className="section-header">
            <h2 data-cms-field="heading">{cta.heading}</h2>
            <p style={{ color: 'rgba(255,255,255,0.8)' }} data-cms-field="subheading">
              {cta.subheading}
            </p>
            <div style={{ marginTop: '2rem' }}>
              <Link to={cta.ctaLink} className="btn btn-primary btn-lg" data-cms-field="ctaText" data-cms-link-field="ctaLink">{cta.ctaText}</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default About;
